import { useCallback } from 'react';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { HubResourcesCount } from '@lp/features/RoutePlanner/types';
import { useOrDateTimeState } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks';
import { getTimeZonedDate } from '@/utils/helpers';
import { useDevicesAvailability } from '@/queries/resource/availability';
import { useClinicalSpecialistsForAssignment } from '@/queries/resource';
import { Hub } from '@/api/apis/facilities';
import { OrganType } from '@/api/apis/case';
import { CSAssignmentCandidate, EmployeeRole, OcsConsoleData } from '@/api/apis/resources';

type UseHubResourcesType = {
  isLoading: boolean;
  getDevices: (hub: Hub) => OcsConsoleData[];
  getClinicalSpecialists: (hub: Hub) => CSAssignmentCandidate[];
  getCountedResources: (hub?: Hub | null) => HubResourcesCount;
};

export const useHubResources = ({ enabled }: { enabled: boolean }): UseHubResourcesType => {
  const { caseId, organType, donorType, donorHospitalDate, donorHospitalTime, donorHospitalTimeZone } =
    useCaseDetailsWithReviews();
  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const { orDateTime } = useOrDateTimeState(orCaseDateTime);

  const { data: devicesList, isFetching: isDevicesFetching } = useDevicesAvailability(
    { caseId, reserved: true },
    enabled,
  );
  const { data: csCandidates, isFetching: isCsCandidatesFetching } = useClinicalSpecialistsForAssignment(
    {
      caseId,
      donor: donorType,
      organ: organType,
      orTime: orDateTime.toAbsoluteString(),
    },
    enabled,
  );

  const getDevices = useCallback(
    (hub: Hub) => {
      return devicesList?.filter((d) => d.nopHub === hub.id && !d.isInMaintenance) || [];
    },
    [devicesList],
  );

  const getClinicalSpecialists = useCallback(
    (hub: Hub) => {
      return csCandidates?.filter((i) => i.shiftHubId === hub.id) || [];
    },
    [csCandidates],
  );

  const getCountedResources = useCallback(
    (hub?: Hub | null) => {
      if (!hub) {
        return {
          [OrganType.Lungs]: 0,
          [OrganType.Liver]: 0,
          [OrganType.Heart]: 0,
          [OrganType.Kidney]: 0,
          [EmployeeRole.ClinicalSpecialist]: 0,
        };
      }

      const devices = getDevices(hub);
      const clinicalSpecialists = getClinicalSpecialists(hub);

      const devicesCount = devices.reduce(
        (acc, d) => {
          return {
            ...acc,
            [d.organType]: acc[d.organType] + 1,
          };
        },
        { [OrganType.Lungs]: 0, [OrganType.Liver]: 0, [OrganType.Heart]: 0, [OrganType.Kidney]: 0 },
      );

      return {
        ...devicesCount,
        [EmployeeRole.ClinicalSpecialist]: clinicalSpecialists.length,
      };
    },
    [getDevices, getClinicalSpecialists],
  );

  return {
    isLoading: isDevicesFetching || isCsCandidatesFetching,
    getDevices,
    getClinicalSpecialists,
    getCountedResources,
  };
};
